/* @flow */
import hoistNonReactStatic from 'hoist-non-react-statics';
import invariant from 'invariant';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { omit } from 'lodash';

import userAgent from 'lib/currentUserAgent';

export const layerStack = [];

function shouldHideLayerOnEvent(layer, event) {
  // $FlowFixMe type what stack array is
  if (!layer.props.hideOnBlur) {
    return false;
  }

  // $FlowFixMe where does context come from
  const context = layer.props.context;
  if (context && context.contains(event.target)) {
    return false;
  }

  const node = ReactDOM.findDOMNode(layer);
  invariant(node && 'contains' in node, 'withLayerHideOnBlurTrigger findDOMNode returned faulty');
  return !node.contains(event.target);
}

export function hideOnBlur(event: Object) {
  let layersToHide = [];
  const layersToKeep = [];

  // split the layers into 2 bucket.
  let i = layerStack.length;
  while (i--) {
    const layer = layerStack[i];
    if (shouldHideLayerOnEvent(layer, event)) {
      layersToHide.push(layer);
    } else {
      layersToKeep.push(layer);
    }
  }

  // If a layer has a button that open another layer
  // then clicking inside the second layer should not close
  // the first layer.
  layersToHide = layersToHide.filter(layerToHide => {
    // $FlowFixMe where does context come from
    const contextToHide = layerToHide.props.context;

    const shouldFilterOut = layersToKeep.some(layerToKeep => {
      const nodeToKeep = ReactDOM.findDOMNode(layerToKeep);
      invariant(
        nodeToKeep instanceof window.HTMLElement,
        'layersToHide findDOMNode returned non HTMLElement'
      );
      return !nodeToKeep.contains(contextToHide);
    });

    return !shouldFilterOut;
  });

  layersToHide.forEach(layer => {
    // Enqueue this at the end of the event queue to allow click events to fire in IE
    // $FlowFixMe type the layers
    setTimeout(layer.props.onRequestHide, 0);
  });
}

function withLayerHideOnBlurTrigger<Props: {}>(
  Child: React.ComponentType<{ hideOnBlur?: boolean } & Props>
) {
  class LayerHideOnBlur extends React.Component<Props> {
    static defaultProps = {
      hideOnBlur: true,
    };

    // $FlowFixMe property `displayName` not found in Component object type
    displayName = `withLayerHideOnBlurTrigger(${Child.displayName})`;

    componentDidMount() {
      if (layerStack.length === 0) {
        if (userAgent.firefox()) {
          document.addEventListener('focus', hideOnBlur, true);
        } else {
          document.addEventListener('focusin', hideOnBlur, false);
        }
        document.addEventListener('mousedown', hideOnBlur, false);
      }

      layerStack.push(this);
    }

    componentWillUnmount() {
      const index = layerStack.indexOf(this);
      if (index >= 0) {
        layerStack.splice(index, 1);

        if (layerStack.length === 0) {
          if (userAgent.firefox()) {
            document.removeEventListener('focus', hideOnBlur, true);
          } else {
            document.removeEventListener('focusin', hideOnBlur, false);
          }
          document.removeEventListener('mousedown', hideOnBlur, false);
        }
      }
    }

    render() {
      const childProps = omit(this.props, 'hideOnBlur');
      return <Child {...childProps} />;
    }
  }
  return hoistNonReactStatic(LayerHideOnBlur, Child);
}

export default withLayerHideOnBlurTrigger;
